import { MenuBurgerIcon, SelectOptionProps, TableSortBy } from '@acceso/design-system';
import { debounce } from 'lodash';
import { create } from 'zustand';

import { PaginationValues } from '@/hooks/usePagination';
import {
  DEFAULT_PAGE_SIZE,
  TableKeyEnum,
  useTableSettingsStore,
} from '@/hooks/useTableSettingsStore';
import { ParamsWithPagination } from '@/types/api';
import { TableFiltersState } from '@/types/table-filters';
import { NestedNullable } from '@/types/types';

type CreateTableFiltersStateParams<Filters> = {
  defaultFilterValues?: Partial<Filters>;
  tableKey?: TableKeyEnum;
};

const DEFAULT_PAGINATION_VALUES: PaginationValues = {
  currentPage: 1,
  offset: 0,
  rowsPerPage: DEFAULT_PAGE_SIZE,
  query: '',
  sortBy: {
    id: null,
    order: null,
  },
};

/**
 * Narrowing a list must move `currentPage` and `offset` together. The request derives
 * its window from `currentPage` (see getParamsFromTableFilters), but `offset` is part of
 * the shared PaginationValues and would otherwise report a position the list is not on.
 */
export function resetToFirstPage(paginationValues: PaginationValues): PaginationValues {
  return {
    ...paginationValues,
    currentPage: 1,
    offset: 0,
  };
}

export function createTableFiltersState<Filters>({
  defaultFilterValues = {},
  tableKey,
}: CreateTableFiltersStateParams<Filters> = {}) {
  return create<TableFiltersState<NestedNullable<Partial<Filters>>>>(set => {
    const setFilters = (newFilters: NestedNullable<Partial<Filters>>) =>
      set(state => ({
        filters: {
          ...state.filters,
          ...newFilters,
        },
        paginationValues: resetToFirstPage(state.paginationValues),
      }));
    const clearFilters = () =>
      set(state => ({
        filters: { ...defaultFilterValues },
        paginationValues: resetToFirstPage(state.paginationValues),
      }));

    return {
      filters: defaultFilterValues,
      paginationValues: tableKey
        ? {
            ...DEFAULT_PAGINATION_VALUES,
            rowsPerPage: useTableSettingsStore.getState().getRowsPerPage(tableKey),
          }
        : DEFAULT_PAGINATION_VALUES,
      setSearchQuery: debounce(
        query =>
          set(state => ({
            paginationValues: resetToFirstPage({ ...state.paginationValues, query }),
          })),
        400
      ),
      setPage: (page: number) =>
        set(state => ({
          paginationValues: {
            ...state.paginationValues,
            currentPage: page,
            offset: (page - 1) * state.paginationValues.rowsPerPage,
          },
        })),
      setRows: (rowsPerPage: number) => {
        if (tableKey) {
          useTableSettingsStore.getState().setRowsPerPage(tableKey, rowsPerPage);
        }
        set(state => ({
          paginationValues: resetToFirstPage({ ...state.paginationValues, rowsPerPage }),
        }));
      },
      setSortBy: (sortBy: TableSortBy) =>
        set(state => ({
          paginationValues: { ...state.paginationValues, sortBy },
        })),
      setFilters,
      clearFilters,
    };
  });
}

type CreateFilterOptionsParams<OptionType> = {
  options: SelectOptionProps<OptionType>[];
  allOptionLabel: string;
};

export function createTableFilterOptions<OptionType>({
  options,
  allOptionLabel,
}: CreateFilterOptionsParams<OptionType>): SelectOptionProps<OptionType | ''>[] {
  return [
    {
      label: allOptionLabel,
      value: '',
      icon: MenuBurgerIcon,
    },
    ...options,
  ];
}

type GetParamsFromTableFiltersParams<Filters> = {
  filters: Filters;
  paginationValues: PaginationValues;
};

export function getParamsFromTableFilters<Filters>({
  filters,
  paginationValues,
}: GetParamsFromTableFiltersParams<Filters>): ParamsWithPagination<Filters> & { limit: number } {
  return {
    ...filters,
    offset: (paginationValues.currentPage - 1) * paginationValues.rowsPerPage,
    limit: paginationValues.rowsPerPage,
    search: paginationValues.query,
    order_by:
      paginationValues.sortBy?.id && paginationValues.sortBy?.order
        ? `${paginationValues.sortBy.id} ${paginationValues.sortBy.order}`
        : null,
  };
}
